import { useCallback, useEffect, useState } from 'react';
import { useAppRefresh } from '../../hooks/useAppRefresh';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import AlertList from '../../components/cultivar/AlertList';
import {
  Shield, Scale, ClipboardList, Award, AlertTriangle, CheckCircle,
  FileText, Loader2, RefreshCw, ExternalLink, ChevronRight
} from 'lucide-react';
import type { ComplianceDashboard as DashboardData, ComplianceAlert } from '../../types/cultivar';
import cultivarService from '../../services/cultivar';

function ComplianceDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [alerts, setAlerts] = useState<ComplianceAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  useAppRefresh(useCallback(() => { loadDashboard(); }, []));

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);

    try {
      const [dashboardRes, alertsRes] = await Promise.all([
        cultivarService.getComplianceDashboard(),
        cultivarService.getAlerts({ status: 'pending' }),
      ]);

      if (dashboardRes.success && dashboardRes.data) {
        setDashboard(dashboardRes.data);
      }
      if (alertsRes.success && alertsRes.data) {
        setAlerts(alertsRes.data.alerts);
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Failed to load compliance dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAlerts = async () => {
    setRefreshing(true);
    try {
      const response = await cultivarService.generateAlerts();
      if (response.success) {
        loadDashboard();
      }
    } catch (err) {
      console.error('Error generating alerts:', err);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Compliance Dashboard"
          icon={<Shield className="h-6 w-6 text-white" />}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Compliance Dashboard"
          icon={<Shield className="h-6 w-6 text-white" />}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-error-600 mb-4">{error}</p>
              <Button variant="outline" onClick={loadDashboard}>
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const criticalAlerts = alerts.filter(a => a.severity === 'critical').length;
  const warningAlerts = alerts.filter(a => a.severity === 'warning').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Compliance Dashboard"
        subtitle="Monitor your cultivar compliance status and upcoming deadlines"
        icon={<Shield className="h-6 w-6 text-white" />}
        actionButtons={
          <Button
            variant="outline"
            onClick={handleGenerateAlerts}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh Alerts
          </Button>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Alert Summary */}
        {alerts.length > 0 && (
          <div className={`
            mb-6 p-4 rounded-xl flex items-center justify-between
            ${criticalAlerts > 0
              ? 'bg-error-50 border border-error-200'
              : 'bg-warning-50 border border-warning-200'
            }
          `}>
            <div className="flex items-center gap-3">
              <AlertTriangle className={`h-6 w-6 ${
                criticalAlerts > 0 ? 'text-error-600' : 'text-warning-600'
              }`} />
              <div>
                <p className={`font-medium ${
                  criticalAlerts > 0 ? 'text-error-900' : 'text-warning-900'
                }`}>
                  {alerts.length} Active Alert{alerts.length !== 1 ? 's' : ''} Requiring Attention
                </p>
                <p className={`text-sm ${
                  criticalAlerts > 0 ? 'text-error-700' : 'text-warning-700'
                }`}>
                  {criticalAlerts > 0 && `${criticalAlerts} critical, `}
                  {warningAlerts} warning{warningAlerts !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <Button
              variant={criticalAlerts > 0 ? 'danger' : 'primary'}
              size="sm"
              onClick={() => document.getElementById('alerts-section')?.scrollIntoView({ behavior: 'smooth' })}
            >
              View Alerts
            </Button>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/cultivar/licenses')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Scale className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-neutral-900">
                    {dashboard?.active_licenses || 0}
                  </p>
                  <p className="text-sm text-neutral-500">Active Licenses</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/cultivar/declarations')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-warning-100 flex items-center justify-center">
                  <ClipboardList className="h-6 w-6 text-warning-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-neutral-900">
                    {dashboard?.pending_licenses || 0}
                  </p>
                  <p className="text-sm text-neutral-500">Pending Licenses</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/cultivar/certificates')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-success-100 flex items-center justify-center">
                  <Award className="h-6 w-6 text-success-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-neutral-900">
                    {dashboard?.active_licenses || 0}
                  </p>
                  <p className="text-sm text-neutral-500">Active Licenses</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-neutral-900">
                    {dashboard?.expiring_documents_30d || 0}
                  </p>
                  <p className="text-sm text-neutral-500">Docs Expiring Soon</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Alerts List */}
          <div className="lg:col-span-2">
            <Card id="alerts-section">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-warning-600" />
                  Active Alerts
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/cultivar/compliance/alerts')}
                >
                  View All
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <AlertList alerts={alerts.slice(0, 10)} onAlertUpdated={loadDashboard} />
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/cultivar')}
                >
                  <Scale className="h-4 w-4" />
                  Browse Cultivar Registry
                  <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/cultivar/licenses')}
                >
                  <Scale className="h-4 w-4" />
                  Manage Licenses
                  <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/cultivar/declarations')}
                >
                  <ClipboardList className="h-4 w-4" />
                  Submit Declaration
                  <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/cultivar/certificates')}
                >
                  <Award className="h-4 w-4" />
                  View Certificates
                  <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
                </Button>
              </CardContent>
            </Card>

            {/* Compliance Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-success-600" />
                  Compliance Tips
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm text-neutral-600">
                  <li className="flex items-start gap-2">
                    <span className="text-success-500 mt-0.5">•</span>
                    <span>Keep certificates on file for GlobalGAP/HARPS audits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success-500 mt-0.5">•</span>
                    <span>Submit declarations promptly after each season</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success-500 mt-0.5">•</span>
                    <span>Monitor license expiry dates and renew in advance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success-500 mt-0.5">•</span>
                    <span>Confirm pending licenses within 7 days</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ComplianceDashboard;
